import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Shield, AlertTriangle, CheckCircle2, XCircle, Lock, Unlock, RefreshCw,
  CreditCard, TrendingUp, Activity, Clock, Filter, User, Zap,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const SEVERITY_STYLE = {
  info: 'bg-blue-500/10 text-blue-300 border-blue-500/25',
  warning: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
  critical: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
};

const EVENT_STYLE = {
  login_success: { icon: CheckCircle2, tint: 'text-emerald-400' },
  login_fail: { icon: XCircle, tint: 'text-rose-400' },
  lockout_triggered: { icon: Lock, tint: 'text-rose-500' },
  logout: { icon: User, tint: 'text-slate-400' },
  password_changed: { icon: Shield, tint: 'text-amber-400' },
  password_reset_requested: { icon: RefreshCw, tint: 'text-amber-400' },
  password_reset_completed: { icon: CheckCircle2, tint: 'text-emerald-400' },
  payment_verified: { icon: CreditCard, tint: 'text-emerald-400' },
  payment_verification_failed: { icon: AlertTriangle, tint: 'text-rose-400' },
  payment_webhook_captured: { icon: CreditCard, tint: 'text-emerald-500' },
  payment_webhook_failed: { icon: AlertTriangle, tint: 'text-rose-400' },
  payment_order_created: { icon: CreditCard, tint: 'text-blue-400' },
  blog_created: { icon: Activity, tint: 'text-blue-400' },
  blog_updated: { icon: Activity, tint: 'text-blue-400' },
  blog_deleted: { icon: XCircle, tint: 'text-amber-400' },
  blog_approved: { icon: CheckCircle2, tint: 'text-emerald-400' },
  blog_rejected: { icon: XCircle, tint: 'text-amber-400' },
  rate_limit_exceeded: { icon: Zap, tint: 'text-orange-400' },
  admin_action: { icon: Shield, tint: 'text-amber-400' },
};

function StatCard({ icon: Icon, label, value, tint = 'text-primary' }) {
  return (
    <div className="glass-card p-4 flex items-center gap-3" data-testid={`sec-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className={`w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center ${tint}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-text-muted uppercase tracking-wide">{label}</div>
        <div className="text-2xl font-heading text-text-main leading-none mt-1">{value ?? '—'}</div>
      </div>
    </div>
  );
}

function EventRow({ ev }) {
  const cfg = EVENT_STYLE[ev.event_type] || { icon: Activity, tint: 'text-slate-400' };
  const Icon = cfg.icon;
  const sevKey = ev.severity || 'info';
  return (
    <tr className="border-t border-primary/8 hover:bg-primary/5" data-testid={`audit-row-${ev.event_type}`}>
      <td className="py-2 px-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${cfg.tint}`} />
          <span className="text-sm text-text-main">{ev.event_type}</span>
        </div>
      </td>
      <td className="py-2 px-3">
        <span className={`px-2 py-0.5 rounded-full text-xs border ${SEVERITY_STYLE[sevKey] || SEVERITY_STYLE.info}`}>
          {sevKey}
        </span>
      </td>
      <td className="py-2 px-3 text-sm text-text-muted">{ev.email || '—'}</td>
      <td className="py-2 px-3 text-sm text-text-muted">{ev.ip_address || '—'}</td>
      <td className="py-2 px-3 text-xs text-text-muted whitespace-nowrap">
        {ev.timestamp ? new Date(ev.timestamp).toLocaleString('en-IN', { hour12: false }) : '—'}
      </td>
      <td className="py-2 px-3">
        {ev.success === false
          ? <XCircle className="w-4 h-4 text-rose-400" />
          : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
      </td>
    </tr>
  );
}

export default function AdminSecurityTab() {
  const [summary, setSummary] = useState(null);
  const [audit, setAudit] = useState({ events: [], total: 0 });
  const [lockedAccounts, setLockedAccounts] = useState({ records: [], active_locked: 0 });
  const [failedPayments, setFailedPayments] = useState({ events: [], count: 0 });
  const [rateLimitHits, setRateLimitHits] = useState({ events: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [filterEvent, setFilterEvent] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [s, a, l, fp, rl] = await Promise.all([
        axios.get(`${API}/api/admin/security/summary`, getAuthHeaders()),
        axios.get(`${API}/api/admin/security/audit-log?limit=50`
          + (filterEvent ? `&event_type=${encodeURIComponent(filterEvent)}` : '')
          + (filterSeverity ? `&severity=${encodeURIComponent(filterSeverity)}` : ''),
          getAuthHeaders()),
        axios.get(`${API}/api/admin/security/locked-accounts`, getAuthHeaders()),
        axios.get(`${API}/api/admin/security/failed-payments?limit=25`, getAuthHeaders()),
        axios.get(`${API}/api/admin/security/rate-limit-hits?limit=25`, getAuthHeaders()),
      ]);
      setSummary(s.data);
      setAudit(a.data);
      setLockedAccounts(l.data);
      setFailedPayments(fp.data);
      setRateLimitHits(rl.data);
    } catch (e) {
      console.error('Security fetch failed', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterEvent, filterSeverity]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const clearLock = async (identifier) => {
    if (!window.confirm(`Unlock ${identifier}?`)) return;
    try {
      await axios.post(`${API}/api/admin/security/locked-accounts/clear`,
        { identifier }, getAuthHeaders());
      fetchAll();
    } catch (e) {
      alert('Failed to unlock: ' + (e.response?.data?.detail || e.message));
    }
  };

  const s24 = summary?.last_24h || {};
  const s7 = summary?.last_7d || {};

  return (
    <div data-testid="admin-security-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading text-3xl text-text-main flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" /> Security Dashboard
          </h2>
          <p className="text-text-muted text-sm mt-1">
            Live audit log, brute-force lockouts, payment failures aur rate-limit hits.
          </p>
        </div>
        <button
          onClick={fetchAll}
          disabled={refreshing}
          className="px-4 py-2 bg-primary/15 text-primary rounded-lg hover:bg-primary/25 transition-colors flex items-center gap-2"
          data-testid="security-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && !summary ? (
        <div className="text-text-muted py-10 text-center">Loading security data…</div>
      ) : (
        <>
          {/* Hero cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard icon={CheckCircle2} label="Logins (24h)" value={s24.login_success} tint="text-emerald-400" />
            <StatCard icon={XCircle} label="Failed Logins (24h)" value={s24.login_fail} tint="text-rose-400" />
            <StatCard icon={Lock} label="Active Lockouts" value={summary?.active_lockouts_now} tint="text-amber-400" />
            <StatCard icon={CreditCard} label="Payments Verified (7d)" value={s7.payments_verified} tint="text-emerald-400" />
            <StatCard icon={AlertTriangle} label="Payment Failures (7d)" value={s7.payment_failures} tint="text-rose-400" />
            <StatCard icon={RefreshCw} label="Password Resets (7d)" value={s7.password_resets} tint="text-blue-400" />
            <StatCard icon={Zap} label="Rate-limit Hits (7d)" value={rateLimitHits.count} tint="text-orange-400" />
            <StatCard icon={Activity} label="Total Events (30d)" value={summary?.total_events_30d} tint="text-primary" />
          </div>

          {/* Locked accounts */}
          <div className="glass-card p-5 mb-6">
            <h3 className="font-heading text-xl text-text-main mb-3 flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-400" /> Locked Accounts ({lockedAccounts.active_locked})
            </h3>
            {lockedAccounts.records.filter(r => r.is_locked).length === 0 ? (
              <p className="text-text-muted text-sm">Koi active lockout nahi hai.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-text-muted text-xs uppercase">
                      <th className="py-2 px-3">Identifier</th>
                      <th className="py-2 px-3">Fails</th>
                      <th className="py-2 px-3">Locked Until</th>
                      <th className="py-2 px-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lockedAccounts.records.filter(r => r.is_locked).map((r) => (
                      <tr key={r.identifier} className="border-t border-primary/8">
                        <td className="py-2 px-3 text-text-main font-mono text-xs">{r.identifier}</td>
                        <td className="py-2 px-3 text-rose-400 font-medium">{r.fails}</td>
                        <td className="py-2 px-3 text-xs text-text-muted">
                          {r.locked_until ? new Date(r.locked_until).toLocaleString('en-IN', { hour12: false }) : '—'}
                        </td>
                        <td className="py-2 px-3">
                          <button
                            onClick={() => clearLock(r.identifier)}
                            className="px-3 py-1 rounded bg-emerald-500/15 text-emerald-300 text-xs hover:bg-emerald-500/25 flex items-center gap-1"
                            data-testid={`unlock-btn-${r.identifier}`}
                          >
                            <Unlock className="w-3 h-3" /> Unlock
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Failed Payments */}
          <div className="glass-card p-5 mb-6">
            <h3 className="font-heading text-xl text-text-main mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-400" /> Recent Failed Payments ({failedPayments.count})
            </h3>
            {failedPayments.events.length === 0 ? (
              <p className="text-text-muted text-sm">Koi payment failure record nahi hai. </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-text-muted text-xs uppercase">
                      <th className="py-2 px-3">Time</th>
                      <th className="py-2 px-3">Reason</th>
                      <th className="py-2 px-3">Session</th>
                      <th className="py-2 px-3">Order</th>
                      <th className="py-2 px-3">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failedPayments.events.map((e, i) => (
                      <tr key={i} className="border-t border-primary/8">
                        <td className="py-2 px-3 text-xs text-text-muted whitespace-nowrap">
                          {new Date(e.timestamp).toLocaleString('en-IN', { hour12: false })}
                        </td>
                        <td className="py-2 px-3 text-rose-300 text-xs">{e.metadata?.reason || '—'}</td>
                        <td className="py-2 px-3 text-text-muted font-mono text-xs">{e.metadata?.session_id?.slice(0, 12) || '—'}</td>
                        <td className="py-2 px-3 text-text-muted font-mono text-xs">{e.metadata?.order_id?.slice(0, 20) || '—'}</td>
                        <td className="py-2 px-3 text-text-muted font-mono text-xs">{e.ip_address}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Rate Limit Hits */}
          <div className="glass-card p-5 mb-6">
            <h3 className="font-heading text-xl text-text-main mb-3 flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-400" /> Rate-limit Hits ({rateLimitHits.count})
            </h3>
            {rateLimitHits.events.length === 0 ? (
              <p className="text-text-muted text-sm">Koi rate-limit trigger nahi hua.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-text-muted text-xs uppercase">
                      <th className="py-2 px-3">Time</th>
                      <th className="py-2 px-3">Path</th>
                      <th className="py-2 px-3">Reason</th>
                      <th className="py-2 px-3">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rateLimitHits.events.map((e, i) => (
                      <tr key={i} className="border-t border-primary/8">
                        <td className="py-2 px-3 text-xs text-text-muted whitespace-nowrap">
                          {new Date(e.timestamp).toLocaleString('en-IN', { hour12: false })}
                        </td>
                        <td className="py-2 px-3 text-orange-300 text-xs font-mono">{e.metadata?.path || '—'}</td>
                        <td className="py-2 px-3 text-text-muted text-xs">{e.metadata?.reason || '—'}</td>
                        <td className="py-2 px-3 text-text-muted font-mono text-xs">{e.ip_address}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Audit Log */}
          <div className="glass-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="font-heading text-xl text-text-main flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" /> Audit Log (latest {audit.count})
              </h3>
              <div className="flex flex-wrap gap-2 items-center">
                <Filter className="w-4 h-4 text-text-muted" />
                <select
                  value={filterEvent}
                  onChange={(e) => setFilterEvent(e.target.value)}
                  className="px-3 py-1.5 rounded bg-surface-alt/50 border border-primary/15 text-text-main text-sm"
                  data-testid="filter-event-type"
                >
                  <option value="">All Events</option>
                  <option value="login_success">login_success</option>
                  <option value="login_fail">login_fail</option>
                  <option value="lockout_triggered">lockout_triggered</option>
                  <option value="password_reset_completed">password_reset_completed</option>
                  <option value="payment_verified">payment_verified</option>
                  <option value="payment_verification_failed">payment_verification_failed</option>
                  <option value="blog_created">blog_created</option>
                  <option value="blog_updated">blog_updated</option>
                  <option value="blog_deleted">blog_deleted</option>
                  <option value="rate_limit_exceeded">rate_limit_exceeded</option>
                  <option value="admin_action">admin_action</option>
                </select>
                <select
                  value={filterSeverity}
                  onChange={(e) => setFilterSeverity(e.target.value)}
                  className="px-3 py-1.5 rounded bg-surface-alt/50 border border-primary/15 text-text-main text-sm"
                  data-testid="filter-severity"
                >
                  <option value="">All Severity</option>
                  <option value="info">info</option>
                  <option value="warning">warning</option>
                  <option value="critical">critical</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-text-muted text-xs uppercase">
                    <th className="py-2 px-3">Event</th>
                    <th className="py-2 px-3">Severity</th>
                    <th className="py-2 px-3">Email</th>
                    <th className="py-2 px-3">IP</th>
                    <th className="py-2 px-3">Time</th>
                    <th className="py-2 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.events.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-6 text-text-muted">No events matched.</td></tr>
                  ) : audit.events.map((e, i) => <EventRow key={i} ev={e} />)}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-text-muted mt-3">
              Showing {audit.count} of {audit.total} events (auto-expires after 90 days).
            </div>
          </div>
        </>
      )}
    </div>
  );
}
